import Menu from '../Menu';
import TopLevelMenu from '../TopLevelMenu';
// Manages the state of the nav, e.g. which level we're on and what data goes with it

const navStateReducer = (state, action) => {
  const {
    levels, menus, currentLevel,
  } = state;
  const { payload, type } = action;

  // When we're done animating, we'll reset state to have one menu and no direction
  if (type === 'reset') {
    const activeMenu = menus.find((menu) => menu.id === levels[currentLevel].id);
    menus.splice(0, menus.length, activeMenu);

    return {
      levels,
      menus,
      currentLevel,
      direction: null,
    };
  }

  // Update levels to track which categories we've moved through
  if (type === 'forward') {
    levels.push({
      id: payload.id,
      label: payload.label,
    });
  } else {
    levels.pop();
  }

  const newLevel = levels.length - 1;

  /**
   * Assembles the props needed for a menu component
   *
   * @param {Array} params.subCategories - The subcategories for this menu level.
   * @param {string|number} params.id - The category ID or footer section ID.
   * @param {string} params.label - The name of the category or footer section.
   * @param {string} [params.espotIdentifier] - The espot identifier, if it's a category.
   * @param {string} [params.allLink] - Link to go straight to the category, e.g. "All Women's"
   * @param {number} newLevel - How deep we will be in the nav hierarchy when this level is added.
   * @returns {Object} The menu data we will put into state.
   */
  const generateMenu = ({
    subCategories, id, label, espotIdentifier, allLink,
  }) => {
    // We will loop through the data and sort the subcats into types
    const regularSubcategories = [];
    const clearanceSubcategories = [];
    const featuredSubcategories = [];
    subCategories?.forEach((category) => {
      // If there are no subcats, espots, or we are already at our depth limit, it's a regular link
      const useLink = (
        newLevel === 3
        || (
          ((!category.navigationSubCategories || category.navigationSubCategories.length === 0)
          && !category.hasEspotContent)
        )
      );

      // Gather things we need to know about the subcat to build a MenuSelector for it
      const thisSubCategory = {
        label: category.title,
        id: category.id,
        hasSpecialNavInteraction: category.hasSpecialNavInteraction,
        link: useLink ? (category.mobileMenuUrl || category.url) : undefined,
        espotIdentifier: category.espotIdentifier,
        isExternal: !!(category.external),
      };

      switch (category.displayType) {
        case 'clearance':
          clearanceSubcategories.push(thisSubCategory);
          break;
        case 'featured':
          featuredSubcategories.push(thisSubCategory);
          break;
        default:
          regularSubcategories.push(thisSubCategory);
          break;
      }
    });

    let newMenu = {};

    // The L0 is a TopLevelMenu with special qualities (has footer links, etc.)
    // Other levels are regular Menus
    if (newLevel === 0) {
      newMenu = {
        id,
        component: {
          type: TopLevelMenu,
          props: {
            primaryLinks: regularSubcategories,
          },
        },
      };
    } else {
      newMenu = {
        id,
        component: {
          type: Menu,
          props: {
            allLink: subCategories.length > 0 ? allLink : undefined,
            clearanceLinks: clearanceSubcategories,
            espotIdentifier,
            regularLinks: regularSubcategories,
            featuredLinks: featuredSubcategories,
            heading: label,
          },
        },
      };
    }
    return newMenu;
  };

  const newMenu = generateMenu({
    subCategories: payload.subCategories,
    id: payload.id,
    label: payload.label,
    espotIdentifier: payload.espotIdentifier,
    allLink: payload.allLink,
  }, newLevel);

  // Add the new menu to the beginning or end of the array, depending on direction
  if (type === 'forward') {
    menus.push(newMenu);
  } else {
    menus.unshift(newMenu);
  }

  return {
    levels,
    menus,
    currentLevel: newLevel,
    direction: type,
  };
};

export default navStateReducer;
